import React, { useEffect, useState } from "react";
import { cloneDeep, remove } from "lodash";
import { useArgusCart } from "../../contexts/ArgusCartContext";
import { confirmationChoices } from "../ConfirmationModal";
import { Box, Button, Container, Grid, Typography, Badge } from "@mui/material";
import { useArgusAuth } from "../../hooks/useArgusAuth";
import { COLORS } from "../../constants";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { CartSummary } from "../CartSummary";
import CartSummaryGrid from "./CartSummaryGrid";

const MobileCart = ({
  loading,
  customerCategory,
  pricingData,
  vat,
  cartNettTotal,
  settlementAmount,
  vatAmount,
  total,
  createCheckout,
  goBack,
}) => {
  const {
    items,
    setItems,
    removeItem,
    emptyCart,
    clearCartMetadata,
    updateItemQuantity,
    totalItems,
  } = useArgusCart();
  const [clearCartModalOpen, setClearCartModalOpen] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});

  const scrollToTop = () => {
    window.scroll(0, 0);
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  const clearCart = () => {
    emptyCart();
    clearCartMetadata();
    setClearCartModalOpen(false);
  };

  const onRemoveItemClick = (sku) => {
    const cartLibItemsCopy = cloneDeep(items);
    remove(cartLibItemsCopy, (x) => x.sku === sku);
    removeItem(sku);
    setItems(cartLibItemsCopy);
  };

  const handleCheckoutClick = async () => {
    if (!isAuthenticated) {
      gotoLoginPage();
      return;
    }
    try {
      await createCheckout();
    } catch (error) {
      console.error(error);
    }
  };

  const goToCheckout = async () => {
    await handleCheckoutClick();
  };

  const handleQuantityChange = (id, sku, value) => {
    const quantity = parseInt(value);
    if (isNaN(quantity) || quantity <= 0) {
      setErrorMessages((prev) => ({
        ...prev,
        [sku]: "Quantity must be a positive number.",
      }));
    } else if (quantity > 500) {
      setErrorMessages((prev) => ({
        ...prev,
        [sku]:
          "For quantities greater than 500, please contact our sales team.",
      }));
    } else {
      setErrorMessages((prev) => ({
        ...prev,
        [sku]: "",
      }));
      updateItemQuantity(id, quantity);
    }
  };

  const handleIncrement = (id, sku, currentQuantity) => {
    if (currentQuantity < 500) {
      updateItemQuantity(id, currentQuantity + 1);
      setErrorMessages((prev) => ({
        ...prev,
        [sku]: "",
      }));
    }
  };

  const handleDecrement = (id, sku, currentQuantity) => {
    if (currentQuantity > 1) {
      updateItemQuantity(id, currentQuantity - 1);
      setErrorMessages((prev) => ({
        ...prev,
        [sku]: "",
      }));
    }
  };

  const { isAuthenticated, loginWithRedirect } =
    useArgusAuth();

  const gotoLoginPage = async () => {
    const returnTo = location.pathname || "/";
    if (!isAuthenticated) {
      await loginWithRedirect({
        appState: { returnTo },
      });
    }
  };

  return (
    <Container sx={{}} disableGutters>
      {loading === false && (
        <Box sx={{ mt: 3 }}>
          {!!items && !!pricingData && items.length > 0 ? (
            <Grid container sx={{ flexDirection: { xs: "column", md: "row" } }}>
              <Grid item xs={12} md={9}>
                <CartSummaryGrid
                  items={items}
                  total={total}
                  totalItems={totalItems}
                  pricingData={pricingData}
                  customerCategory={customerCategory}
                  handleQuantityChange={handleQuantityChange}
                  handleIncrement={handleIncrement}
                  handleDecrement={handleDecrement}
                  onRemoveItemClick={onRemoveItemClick}
                  errorMessages={errorMessages}
                  clearCart={clearCart}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <CartSummary
                  total={total}
                  cartNettTotal={cartNettTotal}
                  settlementAmount={settlementAmount}
                  vat={vat}
                  vatAmount={vatAmount}
                  onButtonClick={goToCheckout}
                  buttonText="Proceed To Checkout"
                />
              </Grid>
              {/* <OftenBoughtTogether
                product={product}
                pricingData={pricingData}
              /> */}
            </Grid>
          ) : (
            <Grid
              item
              xs={12}
              md={9}
              sx={{
                backgroundColor: COLORS.white,
                border: `1px solid ${COLORS.border}`,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                p: 5,
              }}
            >
              <Badge
                badgeContent={totalItems}
                showZero={true}
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: COLORS.themeYellow,
                    color: COLORS.darkText,
                    fontWeight: 600,
                    height: "1rem",
                    width: "1rem",
                    fontSize: "1.5rem",
                    p: "1.25rem",
                    borderRadius: 99,
                  },
                }}
              >
                <ShoppingCartIcon
                  sx={{
                    fontSize: "7rem",
                    color: COLORS.darkText,
                  }}
                />
              </Badge>
              <Typography
                sx={{
                  fontSize: "var(--font-size-m)",
                  fontWeight: 300,
                  mt: 1,
                }}
              >
                Your shopping cart is empty
              </Typography>
              <Button
                size="small"
                variant="contained"
                onClick={goBack}
                sx={{
                  mt: 6,
                  color: COLORS.darkText,
                  backgroundColor: COLORS.themeYellow,
                  boxShadow: "none",
                  px: 5,
                  py: 1,
                }}
              >
                Back to Shopping
              </Button>
            </Grid>
          )}
        </Box>
      )}
    </Container>
  );
};

export default MobileCart;
